import type { ComponentType } from 'react';
import { TriangleAlert, Box, Ban, Ruler } from 'lucide-react';
import type { ConstraintViolation } from '@/hooks/useConstraintCheck';

const CONSTRAINT_LABELS: Record<ConstraintViolation['type'], string> = {
  'build-volume': 'Build volume exceeded',
  'keep-out': 'Keep-out zone violated',
  clearance: 'Clearance too small',
};

const CONSTRAINT_ICONS: Record<
  ConstraintViolation['type'],
  ComponentType<{ className?: string }>
> = {
  'build-volume': Box,
  'keep-out': Ban,
  clearance: Ruler,
};

interface ConstraintWarningPanelProps {
  violations: ConstraintViolation[];
}

/**
 * Amber overlay panel displayed when one or more `@constraint` annotations
 * are violated. Deliberately styled to stand out from the red compile-error
 * panel while still feeling urgent.
 */
export function ConstraintWarningPanel({
  violations,
}: ConstraintWarningPanelProps) {
  if (violations.length === 0) return null;

  return (
    <div className="flex max-w-[400px] flex-col rounded-md bg-amber-500/90 px-3 py-2 text-sm text-white shadow-lg backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center gap-1.5 font-bold">
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
        <span>Constraint violation{violations.length > 1 ? 's' : ''}</span>
      </div>

      {/* Violation entries */}
      <div className="mt-1.5 flex flex-col gap-1">
        {violations.map((v, i) => {
          const Icon = CONSTRAINT_ICONS[v.type];
          const label = CONSTRAINT_LABELS[v.type];
          return (
            <div
              key={i}
              className="flex items-start gap-1.5 rounded bg-white/15 px-2 py-1 font-mono text-xs"
            >
              <Icon className="mt-px h-3 w-3 shrink-0 opacity-90" />
              <span className="break-words">
                {v.message ? v.message : label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
